import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";

const IMG = {
  hero: "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxnb3VybWV0JTIwcGxhdGVkJTIwZm9vZCUyMGZpbmUlMjBkaW5pbmclMjByZXN0YXVyYW50JTIwbHV4dXJ5JTIwZGFya3xlbnwwfHx8fDE3ODg1NjU2ODB8MA&ixlib=rb-4.1.0&q=85",
  s1: "https://images.pexels.com/photos/24186303/pexels-photo-24186303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  s2: "https://images.pexels.com/photos/28616663/pexels-photo-28616663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  s3: "https://images.unsplash.com/photo-1785960160436-242768ecc23a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxnb3VybWV0JTIwcGxhdGVkJTIwZm9vZCUyMGZpbmUlMjBkaW5pbmclMjByZXN0YXVyYW50JTIwbHV4dXJ5JTIwZGFya3xlbnwwfHx8fDE3ODg1NjU2ODB8MA&ixlib=rb-4.1.0&q=85",
  story: "https://images.unsplash.com/photo-1621494268492-d01b98eba7e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwcGxhdGVkJTIwZm9vZCUyMGZpbmUlMjBkaW5pbmclMjByZXN0YXVyYW50JTIwbHV4dXJ5JTIwZGFya3xlbnwwfHx8fDE3ODg1NjU2ODB8MA&ixlib=rb-4.1.0&q=85",
};

const serif = { fontFamily: "'Cormorant Garamond', serif" };
const cinzel = { fontFamily: "'Cinzel', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const SIGNATURES = [
  { img: IMG.s1, name: "Seared Diver Scallop", notes: "Cauliflower · Brown Butter · Winter Truffle" },
  { img: IMG.s2, name: "Wagyu Beef Tartare", notes: "Pickled Shallot · Black Garlic · Aged Yolk" },
  { img: IMG.s3, name: "Valrhona Chocolate Sphere", notes: "Cherry · Hazelnut · Crème Fraîche" },
];
const GOLD = "#D4AF37";

const Overline = ({ children }) => (
  <span className="text-xs tracking-[0.25em] uppercase font-medium" style={{ color: GOLD }}>{children}</span>
);

export default function AureliaDemo() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Aurelia — Fine Dining (Demo) | WebsiteTech.pro"; }, []);

  return (
    <div className="min-h-screen" style={{ background: "#0B0B0C", color: "#FAF9F6", ...body }} data-testid="aurelia-demo">
      {/* demo ribbon */}
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5" style={{ background: "#111827", color: "#F9FAFB" }}>
        WebsiteTech.pro Demo · Fine Dining — click to view all industry demos
      </Link>

      {/* nav */}
      <DemoTopNav
        testid="aurelia-nav"
        sticky
        logo={<div className="text-xl tracking-[0.35em]" style={cinzel}>AURELIA</div>}
        items={["Menu", "Our Story", "Private Dining", "Contact"]}
        ctaLabel="Reserve a Table"
        theme={{ bg: "rgba(11,11,12,0.82)", text: "#FAF9F6", accent: GOLD, accentText: "#0B0B0C", border: `${GOLD}1A` }}
      />

      {/* hero */}
      <header data-testid="aurelia-hero" className="relative overflow-hidden">
        <img src={IMG.hero} alt="Plated fine dining" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0B0B0C 20%, rgba(11,11,12,0.4) 70%, rgba(11,11,12,0.1))" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-44">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <Overline>Seasonal Ingredients · Extraordinary Moments</Overline>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] italic" style={serif}>An Evening<br />to Remember</h1>
            <p className="mt-6 max-w-lg text-lg font-light" style={{ color: "#A1A1AA" }}>Contemporary cuisine, thoughtfully composed — multi-course tasting menus inspired by coastal terroir and the season's harvest.</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="aurelia-menu-btn" className="text-xs tracking-[0.2em] uppercase px-7 py-3.5 rounded-sm font-semibold" style={{ background: GOLD, color: "#0B0B0C" }}>Explore the Menu</button>
              <button className="text-xs tracking-[0.2em] uppercase px-7 py-3.5 rounded-sm" style={{ border: `1px solid ${GOLD}66`, color: "#FAF9F6" }}>Reserve a Table</button>
            </div>
          </motion.div>
        </div>
      </header>

      {/* our story */}
      <section data-testid="aurelia-story" className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img src={IMG.story} alt="The dining room" className="rounded-sm w-full h-[440px] object-cover" />
          <div className="absolute bottom-4 left-4 text-xs tracking-[0.25em] uppercase" style={{ color: GOLD }}>Food · Art · People in Harmony</div>
        </div>
        <div>
          <Overline>Our Story</Overline>
          <h2 className="mt-4 text-4xl md:text-5xl italic" style={serif}>A More Thoughtful Table</h2>
          <p className="mt-6 text-lg font-light leading-relaxed" style={{ color: "#C7C7CC" }}>
            At Aurelia, we believe dining is more than a meal — it's a dialogue between place, season and people.
            Our menu is a celebration of modern cuisine, rooted in exceptional ingredients and a deep respect for
            the art of hospitality.
          </p>
          <button className="mt-7 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase" style={{ color: GOLD }}>Our Story <ArrowRight size={14} /></button>
        </div>
      </section>

      {/* signatures */}
      <section data-testid="aurelia-signatures" className="border-y py-24" style={{ background: "#0e0e10", borderColor: `${GOLD}14` }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div><Overline>Our Signatures</Overline><h2 className="mt-3 text-4xl italic" style={serif}>A season for something extraordinary</h2></div>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {SIGNATURES.map((d, i) => (
              <motion.div key={d.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }}
                className="group rounded-sm overflow-hidden" style={{ background: "#18181C", border: `1px solid ${GOLD}1f` }}>
                <div className="overflow-hidden"><img src={d.img} alt={d.name} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6 text-center">
                  <h3 className="text-2xl" style={serif}>{d.name}</h3>
                  <p className="mt-2 text-xs tracking-[0.15em] uppercase" style={{ color: "#8a8a90" }}>{d.notes}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* tasting quote */}
      <section data-testid="aurelia-tasting" className="max-w-4xl mx-auto px-6 py-28 text-center">
        <div className="text-6xl" style={{ ...serif, color: GOLD }}>&ldquo;</div>
        <p className="text-3xl md:text-4xl italic leading-snug" style={serif}>Aurelia is a rare gem — every course was a masterpiece, and the entire evening felt effortless.</p>
        <p className="mt-6 text-xs tracking-[0.25em] uppercase" style={{ color: "#8a8a90" }}>— A Guest, New York</p>
      </section>

      {/* private dining */}
      <section data-testid="aurelia-private-dining" className="relative overflow-hidden">
        <img src={IMG.s2} alt="Private dining" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "rgba(11,11,12,0.75)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-center">
          <Overline>Private Dining</Overline>
          <h2 className="mt-4 text-4xl md:text-5xl italic" style={serif}>Extraordinary Occasions</h2>
          <p className="mt-5 max-w-xl mx-auto text-lg font-light" style={{ color: "#C7C7CC" }}>Intimate, personal and unforgettable — Aurelia hosts private dining experiences for 8 to 40 guests for life's most meaningful moments.</p>
          <button className="mt-8 text-xs tracking-[0.2em] uppercase px-7 py-3.5 rounded-sm font-semibold" style={{ background: GOLD, color: "#0B0B0C" }}>Enquire About Private Dining</button>
        </div>
      </section>

      {/* footer */}
      <footer data-testid="aurelia-footer" className="border-t py-14" style={{ borderColor: `${GOLD}14` }}>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-sm" style={{ color: "#8a8a90" }}>
          <div><div className="text-lg tracking-[0.3em] mb-3" style={{ ...cinzel, color: "#FAF9F6" }}>AURELIA</div><p>Good food brings people closer.</p></div>
          <div><div className="uppercase tracking-widest text-xs mb-3" style={{ color: GOLD }}>Hours</div>Tue–Sun · 5:30pm–11:00pm<br />Closed Mondays</div>
          <div><div className="uppercase tracking-widest text-xs mb-3" style={{ color: GOLD }}>Visit</div>12 Harbour Lane<br />Smart-elegant dress code</div>
          <div><div className="uppercase tracking-widest text-xs mb-3" style={{ color: GOLD }}>Reserve</div>reservations@aurelia.demo<br />+1 (555) 010-2200</div>
        </div>
      </footer>

      <AIConciergeWidget demo="restaurant" concierge={DEMO_THEMES.restaurant.concierge} />
    </div>
  );
}
